// §4.2 Overview — THE CUSTOMER'S QUOTE PANEL (Module 09, slice P3-C-1).
//
// P3-A's 360 carried one sentence where this panel is: "Quotes, orders and the
// activity trail arrive with the quote and order slices." The quote half has
// arrived, so the sentence is NARROWED to what is still missing rather than
// left standing over a panel that now exists — a stale pending is worse than
// no pending, because the next reader trusts it.
//
// IT IS A LAZY READ, like the reliability history and the effective price
// list beside it. §4.2's budget is < 2 s on a phone during a call, and that
// budget is met by ONE `GET /v1/customers/{id}` — so nothing this panel needs
// rides that request. It fetches the customer's most recent quotes only when
// the Overview is on screen, and a failure drops the panel to a single line
// rather than an error card: the 360 is still worth reading without it.
//
// THE PANEL IS "LATEST", NOT "OPEN", and the i18n key says why: the list
// endpoint filters ONE status at a time and "open" is four of them
// (draft/pending_approval/approved/sent). Rather than fan out four requests
// from a panel, or filter client-side over a cursor page and call the result
// "open", it shows the newest quotes with their status chips — which is the
// same glance and an honest label.
//
// ITS AFFORDANCES ARE GATED ON §4.5's AUDIENCE, NOT ON §4.1's (D-088).
// This panel is mounted for everyone the customer 360 admits — which includes
// finance and planner (CUSTOMER_SCREEN_ROLES) — and the quote BOOK's audience
// (QUOTE_SCREEN_ROLES) is deliberately narrower: ga/fa/sales. The permission
// mirror's own comment says why the panel stays for the wider set ("they read
// them where §4.2 puts them — the customer 360's quote panel"), and that is
// precisely the reason the panel must not LINK them anywhere: every row here
// used to be an <a> to /sales/quotes/{id} and the footer an <a> to
// /sales/quotes, both of which render `ForbiddenState` for a finance or
// planner reader. A link into a refusal is the D-088 defect. So `quote_no` is
// TEXT for a reader outside §4.5's audience and the "see all" line is OMITTED
// (D-225: absence, never a disabled shell or a sentence about what they may
// not do) — the five quotes and their figures are unchanged, because reading
// them here is exactly what §7's read-only row grants.
//
// The gate is `canOpenQuotes`, which is FALSE until the role feed settles —
// the same fail-closed direction the area's entry table uses for the nav
// itself. It cannot shift the layout on resolve: the anchor and the span
// carry the same `quote_no`, so only the ink changes.

import { useTranslation } from "react-i18next";
import { StatusChip } from "../../../../components/status-chip";
import { formatBusinessDate } from "../../../../lib/datetime";
import { todayIso } from "../../../../lib/business-dates";
import { formatMinor } from "../lib/money";
import { quoteStatusVariant, validityBand } from "../lib/quotes";
import { useQuotes } from "../hooks/quote-queries";
import { useSalesPermissions } from "../hooks/permissions";

/** How many rows the panel shows. Small on purpose: this is a GLANCE inside a
 * customer record, and the way to see them all is the link beneath it. */
const PANEL_LIMIT = 5;

export function CustomerQuotesPanel({ customerId }: { customerId: string }) {
  const { t, i18n } = useTranslation();
  const perms = useSalesPermissions();
  const page = useQuotes({ customer_id: customerId, limit: PANEL_LIMIT }, undefined);
  const rows = page.data?.items ?? [];
  const today = todayIso();

  return (
    <div className="ds-card sales-360-card" data-testid="sales-360-quotes">
      <h3>{t("sales.c360.overview.quotesTitle")}</h3>
      {page.error !== null ? (
        // One line, not an ErrorState: a failed side panel must not turn the
        // customer record into an error screen.
        <p className="sales-hint" data-testid="sales-360-quotes-error">
          {t("sales.c360.overview.quotesError")}
        </p>
      ) : null}
      {page.data === undefined && page.error === null && page.fetchStatus === "paused" ? (
        // The read is held until the network returns (design-system §3.9):
        // one line, as the deals and orders panels beside it say it.
        <p className="sales-hint" role="status" data-testid="sales-360-quotes-offline">
          {t("sales.offline.body")}
        </p>
      ) : null}
      {page.error === null && !page.isPending && rows.length === 0 ? (
        <p className="sales-hint" data-testid="sales-360-quotes-empty">
          {t("sales.c360.overview.quotesEmpty")}
        </p>
      ) : null}
      <ul className="sales-360-quote-list">
        {rows.map((quote) => (
          <li key={quote.id} data-testid="sales-360-quote-row" data-status={quote.status}>
            {perms.canOpenQuotes ? (
              <a href={`/sales/quotes/${quote.id}`}>{quote.quote_no}</a>
            ) : (
              <span data-testid="sales-360-quote-no">{quote.quote_no}</span>
            )}
            <StatusChip variant={quoteStatusVariant(quote.status)} dot={false}>
              {t(`sales.quoteStatus.${quote.status}`)}
            </StatusChip>
            {/* §7 omission — no `sales.read_value`, no figure at all. */}
            {quote.total_minor !== undefined ? (
              <span className="num">
                {formatMinor(quote.total_minor, quote.currency, i18n.language)}
              </span>
            ) : null}
            {quote.valid_until !== null ? (
              <span className="num" data-band={validityBand(quote.valid_until, today)}>
                {formatBusinessDate(quote.valid_until, i18n.language)}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      {perms.canOpenQuotes ? (
        <p>
          <a href={`/sales/quotes?customer=${customerId}`} data-testid="sales-360-quotes-more">
            {t("sales.c360.overview.quotesMore")}
          </a>
        </p>
      ) : null}
    </div>
  );
}
